import {
  Banner,
  Box,
  Button,
  Card,
  Grid,
  Input,
  Select,
  Typography,
} from '@oresundsbron/bridge-ui';
import {
  ValidationResult,
  useForm,
  useFormParams,
  useSubscribe,
} from '@oresundsbron/use-form';
import { pipe } from 'fp-ts/lib/function';
import { of } from 'fp-ts/Task';
import {
  fromTask,
  chain,
  map,
  getOrElseW,
  fromPredicate,
  filter,
  match as matchTO,
} from 'fp-ts/lib/TaskOption';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alpr,
  Labels,
  LicensePlateInfo,
  Placeholders,
  VehicleInfo,
} from './types';
import { match } from 'fp-ts/lib/boolean';
import { vehicleInfoPrefill } from './utils';
import { SupportedCountries } from '@oresundsbron/api/src/backend/VehicleInfo';
import { BackendError } from '@oresundsbron/api';
import { useValidateAlpr } from '~/hooks/useValidateAlpr';
import { CountryCodeOptItem } from '~/hooks/useCountries';
import { useTranslation } from 'next-i18next';
import { Notice } from '@oresundsbron/design-system';
import {
  sanitizeLicenseplate,
  useVehicleInformation,
  validateLicenseplateFormat,
} from '@oresundsbron/app-common';

export type AlprProps = {
  labels: Labels & { orderBizz: string };
  countryOptions: CountryCodeOptItem[];
  bvSupportCountries: SupportedCountries;
  fuelTypeOptions: { label: string; value: string }[];
  emissionClassOptions: { label: string; value: string }[];
  weightClassOptions: { label: string; value: string }[];
  onAdd: (data: Alpr) => void;
  onValidate: useFormParams<Alpr>['onValidate'];
  useOnOtherPlacesDisclaimer: string;
  onCancel: () => void;
  displayCancel: boolean;
  placeholders: Placeholders;
  disableForm?: boolean;
  defaultValues?: useFormParams<Alpr>['defaultValues'];
  vehicleColors: { label: string; value: string }[];
  isCommuter?: boolean;
};

const _defaultValues = {
  paymentOptions: 'licensePlate',
  weightClass: 'false',
} as useFormParams<Alpr>['defaultValues'];

export const AlprForm: FC<AlprProps> = ({
  labels,
  countryOptions = [],
  bvSupportCountries,
  weightClassOptions = [],
  fuelTypeOptions = [],
  emissionClassOptions = [],
  onAdd,
  onValidate,
  onCancel,
  displayCancel,
  placeholders,
  disableForm,
  defaultValues,
  vehicleColors,
  isCommuter,
}) => {
  const def = useMemo(
    () => ({ ..._defaultValues, ...defaultValues }),
    [defaultValues]
  );

  const namespace = 'alpr';

  const {
    registerField,
    getError,
    getData,
    validate,
    isValid,
    isDirty,
    transformData,
    resetSelectedData,
    setPartialData,
    resetFieldErrors,
    setFieldErrors,
  } = useForm<Alpr>({
    namespace,
    onValidate,
    defaultValues: def,
  });

  const { t } = useTranslation(['common'], {
    nsMode: 'fallback',
  });

  const [country, licensePlate, fuelType, emissionClass] = useSubscribe<Alpr>(
    namespace,
    'country',
    'licensePlate',
    'fuelType',
    'emissionClass'
  );

  const [plateInfo, setPlateInfo] = useState<LicensePlateInfo | undefined>();
  const [vehicleError, setVehicleError] = useState<BackendError | null>();
  const [vehicleInfoSection, setVehicleInfoSection] = useState<
    VehicleInfo | undefined
  >();
  const [blockCommercial, setBlockCommercial] = useState<boolean>(false);

  const {
    data: vehicleData,
    error: vehicleInforError,
    isFetching: isVehicleFetching,
  } = useVehicleInformation(plateInfo as LicensePlateInfo);

  const validateAlpr = useValidateAlpr();

  useEffect(() => setVehicleError(vehicleInforError), [vehicleInforError]);
  useEffect(() => setVehicleError(null), [licensePlate]);
  useEffect(() => setVehicleInfoSection(vehicleData), [vehicleData]);
  useEffect(() => setVehicleInfoSection(undefined), [licensePlate, country]);
  useEffect(() => setPlateInfo(undefined), [country]);

  useEffect(() => {
    if (fuelType === '5' && emissionClass) {
      setPartialData({
        emissionClass: undefined,
      } as Partial<Alpr>);
    }
  }, [fuelType, setPartialData, emissionClass]);

  const resetVehicleInfoFieldsSet = useCallback(() => {
    resetSelectedData([
      'make',
      'model',
      'color',
      'weightClass',
      'hybrid',
      'fuelType',
      'emissionClass',
    ]);
  }, [resetSelectedData]);

  const resetVehicleInfoFields = useCallback(() => {
    resetVehicleInfoFieldsSet();
    resetFieldErrors();
    setBlockCommercial(false);
    setPlateInfo(undefined);
  }, [resetFieldErrors, resetVehicleInfoFieldsSet]);

  useEffect(() => resetVehicleInfoFields, [country, resetVehicleInfoFields]);

  useEffect(() => {
    vehicleInfoPrefill(
      vehicleInfoSection,
      setPartialData,
      resetVehicleInfoFields,
      emissionClassOptions
    );
    if (vehicleInfoSection?.isCommercialVehicle && isCommuter) {
      setBlockCommercial(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleInfoSection, setPartialData, resetVehicleInfoFields, isCommuter]);

  const validateAndAdd = useCallback(
    () =>
      pipe(
        validate(),
        fromTask,
        chain(fromPredicate((x) => !!x)),
        map(() => onAdd(getData())),
        getOrElseW(() => of(undefined))
      )(),
    [validate, getData, onAdd]
  );

  const onSetPlate = useCallback(
    (plate: string) =>
      pipe(
        { licensePlate: plate, country: country as string },
        fromPredicate((x) => !!x.country && !!x.licensePlate),
        chain(
          validateAlpr<Alpr>({
            licensePlate: labels.licensePlateExist,
          })
        ),
        filter((v) => !!v),
        matchTO(
          () =>
            setPlateInfo({ licensePlate: plate, country: country as string }),
          (errs) => {
            setFieldErrors(errs as ValidationResult<Alpr>);
            resetVehicleInfoFieldsSet();
          }
        )
      )(),
    [
      setPlateInfo,
      country,
      validateAlpr,
      labels,
      setFieldErrors,
      resetVehicleInfoFieldsSet,
    ]
  );

  useEffect(() => {
    if (
      validateLicenseplateFormat(
        sanitizeLicenseplate(licensePlate as string),
        country as string
      )
    ) {
      onSetPlate(licensePlate as string);
    }
  }, [licensePlate, country, onSetPlate]);

  return (
    <Card.Root className="col-span-full">
      <Card.Content>
        {blockCommercial && (
          <Notice>{t('form.validations.commercial.notAllowed')}</Notice>
        )}
        <Grid>
          <Input
            {...registerField('alprName')}
            label={labels.alprName}
            className="col-span-full sm:col-span-4 md:col-span-12"
            error={getError('alprName')}
            disabled={disableForm}
          />
          <Select
            className="col-span-full sm:col-span-4 md:col-span-6"
            {...registerField('country')}
            label={labels.country}
            groupedOptions={countryOptions.map((c) => ({
              children: c.children,
              group: { label: c.group?.label ? t(c.group.label) : undefined },
            }))}
            placeholder={placeholders.country}
            error={getError('country')}
            disabled={disableForm}
            required
          />
          <Input
            {...registerField('licensePlate')}
            label={labels.licensePlate}
            className="col-span-full sm:col-start-5 md:col-span-6 md:col-start-7"
            error={getError('licensePlate')}
            disabled={disableForm}
            required
          />
          {vehicleError ? (
            <Box className="col-span-full bg-white">
              <Banner className="col-span-full mb-2">
                {labels.noVehicleInfo}
              </Banner>
            </Box>
          ) : null}
          <Box className="col-span-full">
            <Typography as="h6" size="lg" className={'mb-0'}>
              {labels.volVehicleInfo}
            </Typography>
          </Box>
          <Input
            {...registerField('make')}
            label={labels.make}
            className="col-span-full sm:col-span-4 md:col-span-6"
            error={getError('make')}
            disabled={disableForm || !!vehicleInfoSection?.make}
          />
          <Input
            {...registerField('model')}
            label={labels.model}
            className="col-span-full sm:col-span-4 md:col-span-6"
            error={getError('model')}
            disabled={disableForm || !!vehicleInfoSection?.model}
          />
          <Select
            className="col-span-full sm:col-span-4 md:col-span-6"
            {...registerField('color')}
            label={labels.color}
            options={vehicleColors}
            placeholderDisabled={false}
            placeholder={placeholders.color}
            error={getError('color')}
            disabled={disableForm || !!vehicleInfoSection?.color}
          />
          <Select
            className="col-span-full sm:col-span-4 md:col-span-6"
            {...registerField('weightClass')}
            label={labels.weightClass}
            options={weightClassOptions}
            placeholder={placeholders.weightClass}
            error={getError('weightClass')}
            placeholderDisabled={false}
            disabled={disableForm || vehicleInfoSection?.isHGV !== undefined}
            required
          />
          <Select
            className="col-span-full sm:col-start-5 md:col-span-6"
            {...registerField('fuelType')}
            label={labels.fuelType}
            options={fuelTypeOptions}
            placeholder={placeholders.fuelType}
            placeholderDisabled={false}
            error={getError('fuelType')}
            disabled={disableForm || !!vehicleInfoSection?.fuelType}
          />
          <Select
            className="col-span-full sm:col-span-4 md:col-span-6"
            {...registerField('emissionClass')}
            label={labels.emissionClass}
            options={emissionClassOptions}
            error={getError('emissionClass')}
            placeholderDisabled={false}
            placeholder={placeholders.emissionClass}
            disabled={
              disableForm ||
              !!vehicleInfoSection?.euroEmissionNorm ||
              fuelType === '5'
            }
          />
          <Input
            {...registerField('hybrid', {
              type: 'checkbox',
              value: 'true',
            })}
            label={labels.hybrid}
            className="col-span-full sm:col-start-5 md:col-span-6"
            error={getError('hybrid')}
            disabled={disableForm || vehicleInfoSection?.isHybrid !== undefined}
          />
          <Input
            name="orderBizz"
            type="checkbox"
            value="true"
            label={labels.orderBizz}
            className="col-span-full sm:col-start-5 md:col-span-6"
            onChange={(e) =>
              pipe(
                e.target.checked,
                match(
                  () =>
                    transformData((curr) => ({
                      ...curr,
                      paymentOptions: 'licensePlate',
                    })),
                  () =>
                    transformData((curr) => ({
                      ...curr,
                      paymentOptions: 'bizz',
                    }))
                )
              )
            }
            disabled={disableForm}
          />
          <Box className="col-span-full mt-2 flex justify-end">
            {displayCancel ? (
              <Button
                intent="text"
                onClick={onCancel}
                className="mr-auto"
                disabled={disableForm}
              >
                {labels.cancel}
              </Button>
            ) : null}
            <Button
              intent="outlined"
              onClick={validateAndAdd}
              disabled={
                !isValid ||
                !isDirty ||
                disableForm ||
                blockCommercial ||
                isVehicleFetching
              }
              className="ml-auto"
            >
              {labels.add}
            </Button>
          </Box>
        </Grid>
      </Card.Content>
    </Card.Root>
  );
};
